import { MultipleChoiceExamStatics, MultipleChoiceExamExaminerData } from '../../../../../../../store/workspace/types';
import { CheckSolidIcon, ChevronDownIcon, TimesSolidIcon } from '../../../../../../../assets/icons';
import MotionWrapper from '../../../../../../../layouts/MotionWrapper';
import DropDown from '../../../../../../../components/DropDown';
import { useState } from 'react';
import { QuestionChoices, QuestionText } from '../../../../components/QuestionContent';
import EmptyState from '../../../../components/EmptyState';
import { QUESTION_LEVEL_FILTERS, QUESTION_TYPE_FILTERS } from '../../../../constants';
import { useFilteredQuestions } from '../../../../hooks/useFilteredQuestions';
import { useExpandableRows } from '../../../../hooks/useExpandableRows';
import styles from './style.module.scss';

/** Score columns of the overview table, grouped as the header presents them. */
const SCORE_GROUPS: {
    title: string;
    columns: { title: string; score: keyof MultipleChoiceExamExaminerData['scores']; total: keyof MultipleChoiceExamExaminerData['scores'] }[];
}[] = [
        {
            title: 'Question levels',
            columns: [
                { title: 'Easy', score: 'score_easy_level', total: 'n_questions_easy' },
                { title: 'Medium', score: 'score_medium_level', total: 'n_questions_medium' },
                { title: 'Hard', score: 'score_hard_level', total: 'n_questions_hard' },
            ],
        },
        {
            title: 'Question types',
            columns: [
                { title: 'General', score: 'score_general', total: 'n_questions_general' },
                { title: 'Technical', score: 'score_technical', total: 'n_questions_technical' },
                { title: 'Coding', score: 'score_coding', total: 'n_questions_coding' },
            ],
        },
    ];

const SCORE_COLUMNS = SCORE_GROUPS.flatMap(group => group.columns);

/** The applicant leaves a question blank by answering `-1`. */
const BLANK_ANSWER = '-1';

interface Props {
    examData: MultipleChoiceExamStatics | null;
    examinerData: MultipleChoiceExamExaminerData | undefined;
};

export default function MultipleChoiceExamResults(props: Props) {
    const { examData, examinerData } = props;

    const [showMoreReport, setShowMoreReport] = useState(false);
    const { isExpanded, toggleRow } = useExpandableRows<number>();
    const {
        questions,
        questionType,
        questionLevel,
        setQuestionType,
        setQuestionLevel,
    } = useFilteredQuestions(examData);

    const renderAnswerResult = (questionIndex: number, answer: number) => {
        const given = `${examinerData?.answers[questionIndex]}`;

        if (given === BLANK_ANSWER) return <span>Blank</span>;
        return given === `${answer}` ? <CheckSolidIcon /> : <TimesSolidIcon />;
    };

    if (!examinerData) {
        return (
            <MotionWrapper className={styles.container}>
                <EmptyState className={styles.empty_state} />
            </MotionWrapper>
        );
    }

    return (
        <MotionWrapper className={styles.container}>
            <div className={styles.header}>
                <div className={styles.header_top}>
                    <span className={styles.header_top_title}>{examData?.exam?.jobTitle}</span>
                    <div className={styles.header_top_actions}>
                        <DropDown
                            idKey='id'
                            titleKey='title'
                            label='Questions level'
                            setItem={setQuestionLevel}
                            selectedItem={questionLevel}
                            dropDownList={QUESTION_LEVEL_FILTERS}
                            containerClassName={styles.header_top_actions_dropdown}
                        />
                        <DropDown
                            idKey='id'
                            titleKey='title'
                            label='Questions type'
                            setItem={setQuestionType}
                            selectedItem={questionType}
                            dropDownList={QUESTION_TYPE_FILTERS}
                            containerClassName={styles.header_top_actions_dropdown}
                        />
                    </div>
                </div>
                <div className={styles.header_bottom}>
                    <span className={styles.header_bottom_item}>
                        Applicant Name : {examinerData.first_name} {examinerData.family_name}
                    </span>
                    <span className={styles.header_bottom_item}>
                        Question Quantities : {examData?.exam?.n_questions}
                    </span>
                    <span className={styles.header_bottom_item}>
                        Exam Time : {examData?.exam?.time}'
                    </span>
                </div>
            </div>

            <div className={styles.overview_table}>
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            {SCORE_GROUPS.map(group => <th key={group.title} colSpan={group.columns.length}>
                                <span className={styles.head_section}>{group.title}</span>
                            </th>)}
                        </tr>
                        <tr>
                            <th>Grade</th>
                            {SCORE_COLUMNS.map(column => <th key={column.title}>{column.title}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{examinerData.scores.score_overall}/{examinerData.scores.max_score}</td>
                            {SCORE_COLUMNS.map(column => <td key={column.title}>
                                {examinerData.scores[column.score]}/{examinerData.scores[column.total]}
                            </td>)}
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className={styles.report} data-is-full-view={showMoreReport}>
                <p><b>Report: </b>{examinerData.feedback_report || '----'}</p>
                <div className={styles.report_more}>
                    <div
                        className={styles.report_more_btn}
                        data-is-full-view={showMoreReport}
                        onClick={() => setShowMoreReport(prev => !prev)}
                    >
                        {showMoreReport ? 'Less' : 'More'} <ChevronDownIcon />
                    </div>
                </div>
            </div>

            <div className={styles.table}>
                <table>
                    <thead>
                        <tr>
                            <th>Questions & Answers</th>
                            <th>Skill Category</th>
                            <th>Level</th>
                            <th>Type</th>
                            <th>Result</th>
                        </tr>
                    </thead>
                    <tbody>
                        {questions.map(({ choices, index, answer, question, level, type, skill_category }, position) => <tr key={index}>
                            <td>
                                <QuestionText
                                    styles={styles}
                                    question={question}
                                    prefix={`${position + 1} - `}
                                />
                                <QuestionChoices
                                    styles={styles}
                                    choices={choices}
                                    answer={answer}
                                    examinerAnswer={examinerData.answers[index]}
                                />
                            </td>
                            <th style={{ textTransform: 'capitalize' }}>{skill_category || '-'}</th>
                            <th style={{ textTransform: 'capitalize' }}>{level}</th>
                            <th style={{ textTransform: 'capitalize' }}>{type}</th>
                            <td>{renderAnswerResult(index, answer)}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>

            <div className={styles.mobile_list}>
                {questions.map(({ choices, index, answer, question, level, type, skill_category }, position) => <div
                    key={index}
                    className={styles.card}
                    onClick={() => toggleRow(position)}
                >
                    <div className={styles.card_header}>
                        <span>Question {position + 1}</span>
                        <ChevronDownIcon style={{ transform: isExpanded(position) ? 'rotate(180deg)' : 'none' }} />
                    </div>

                    {isExpanded(position) && <div className={styles.card_body}>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_content}>
                                <QuestionText styles={styles} question={question} codeBlock='auto' />
                                <QuestionChoices styles={styles} choices={choices} answer={answer} />
                            </div>
                        </div>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Skill Category</span>
                                <span>{skill_category || '-'}</span>
                            </div>
                        </div>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Level</span>
                                <span>{level}</span>
                            </div>
                        </div>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Type</span>
                                <span>{type}</span>
                            </div>
                        </div>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Result</span>
                                <span>{renderAnswerResult(index, answer)}</span>
                            </div>
                        </div>
                    </div>}
                </div>)}
            </div>
        </MotionWrapper>
    );
};
